<template>
  <div>
    <v-card>
      <v-card-title class="text-h6">
        {{ $t('Supporting_Documents') }} {{ documents.length ? `(${documents.length})` : '(0)' }}
        <template v-if="canShowShowButton">
          <v-tooltip :text="show ? $t('Hide') : $t('Show')" location="bottom">
            <template v-slot:activator="{ props }">
              <v-icon @click="show = !show" v-bind="props">{{ show ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
            </template>
          </v-tooltip>
        </template>
        <v-tooltip :text="$t('Download_Selected')" location="bottom" v-if="documents.length">
          <template v-slot:activator="{ props }">
            <v-btn icon variant="text" v-bind="props" :disabled="!selectedFileNames.length" @click="downloadSelected">
              <v-icon>mdi-download-outline</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
      </v-card-title>
      <v-card-text v-if="show">
        <div v-if="gettingDocuments">{{ $t('Retrieving_Documents') }}...</div>
        <template v-else>
          <v-container class="hidden-md-and-up">
            <v-row dense v-if="documents.length">
              <v-col cols="12" sm="6" md="6" v-for="doc in documents" v-bind:key="doc.id">
                <v-card>
                  <v-container>
                    <v-row dense>
                      <v-col cols="1">
                        <v-checkbox-btn v-model="selectedFileNames" :value="doc.fileName" density="compact"></v-checkbox-btn>
                      </v-col>
                      <v-col>
                        <div>
                          <a href="#" class="noUnder" @click.prevent="downloadDoc(doc)">
                            {{ doc.fileName }}
                          </a>
                        </div>
                        <div>{{ (doc.modifiedBy || {}).displayName }}</div>
                        <div>{{ $filters.DateTimeFilter(doc.modifiedByDate) }}</div>
                        <div v-if="showOpenText">
                          <div class="openTextLabel">{{ $t('Upload_To_OpenText') }}</div>
                          <v-radio-group
                            :model-value="openTextValue(doc)"
                            inline
                            density="compact"
                            hide-details
                            readonly
                          >
                            <v-radio :label="$t('Yes')" value="yes"></v-radio>
                            <v-radio :label="$t('No')" value="no"></v-radio>
                          </v-radio-group>
                        </div>
                      </v-col>
                      <v-col cols="1">
                        <a href="#" class="noUnder" @click.prevent="setupDocDetails(doc)">
                          <v-icon>mdi-dots-horizontal</v-icon>
                        </a>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card>
              </v-col>
            </v-row>
            <v-row dense v-else>
              <v-col>
                {{ $t('No_Results') }}
              </v-col>
            </v-row>
          </v-container>
          <v-data-table class="hidden-sm-and-down" :headers="dataTableHeaders" :items="documents">
            <template v-slot:[`header.download`]>
              <div class="d-flex flex-column align-start">
                <span>{{ $t('Download') }}</span>
                <v-checkbox-btn
                  class="flex-grow-0"
                  :model-value="allSelected"
                  :indeterminate="someSelected && !allSelected"
                  density="compact"
                  @update:model-value="toggleSelectAll"
                ></v-checkbox-btn>
              </div>
            </template>
            <template v-slot:item="props">
              <tr>
                <td>
                  <div class="d-flex justify-start">
                    <v-checkbox-btn
                      class="flex-grow-0"
                      v-model="selectedFileNames"
                      :value="props.item.fileName"
                      density="compact"
                    ></v-checkbox-btn>
                  </div>
                </td>
                <td>
                  <a href="#" class="noUnder" @click.prevent="downloadDoc(props.item)">
                    {{ props.item.fileName }}
                  </a>
                </td>
                <td>{{ (props.item.modifiedBy || {}).displayName }}</td>
                <td>{{ $filters.DateTimeFilter(props.item.modifiedByDate) }}</td>
                <td v-if="showOpenText">
                  <v-radio-group :model-value="openTextValue(props.item)" inline density="compact" hide-details readonly>
                    <v-radio :label="$t('Yes')" value="yes"></v-radio>
                    <v-radio :label="$t('No')" value="no"></v-radio>
                  </v-radio-group>
                </td>
                <td>
                  <a href="#" class="noUnder" @click.prevent="setupDocDetails(props.item)">
                    <v-icon>mdi-dots-horizontal</v-icon>
                  </a>
                </td>
              </tr>
            </template>
          </v-data-table>
        </template>
      </v-card-text>
    </v-card>

    <toaster ref="toaster"></toaster>

    <v-dialog v-model="downloading" persistent max-width="400">
      <v-card>
        <v-card-text>
          <div class="mb-2">{{ $t('Downloading_Files', { completed: downloadCompleted, total: downloadTotal }) }}</div>
          <v-progress-linear :model-value="downloadTotal ? (downloadCompleted / downloadTotal) * 100 : 0"></v-progress-linear>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="info" text @click="cancelDownloadSelected">{{ $t('Cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDocDetails" max-width="500">
      <v-card>
        <v-app-bar color="blue-grey" dense></v-app-bar>
        <v-card-title>{{ detailsDoc.fileName }}</v-card-title>
        <v-card-text>
          <v-container>
            <v-row dense>
              <v-col>
                <div>{{ $t('Created_By') }}: {{ (detailsDoc.insertedBy || {}).displayName }}</div>
                <div>{{ $t('Created_On') }}: {{ $filters.DateTimeFilter(detailsDoc.insertedByDate) }}</div>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col>
                <div>{{ $t('Modified_By') }}: {{ (detailsDoc.modifiedBy || {}).displayName }}</div>
                <div>{{ $t('Modified_On') }}: {{ $filters.DateTimeFilter(detailsDoc.modifiedByDate) }}</div>
              </v-col>
            </v-row>
            <strong>{{ $t('Versions') }}</strong>
            <template v-for="(version, index) in reversedDocVersions" :key="index">
              <v-row dense align="center">
                <v-col cols="1">
                  <a href="#" class="noUnder" @click.prevent="downloadDoc(detailsDoc, version.versionId)">
                    <v-icon>mdi-cloud-download-outline</v-icon>
                  </a>
                </v-col>
                <v-col>
                  <div>{{ $t('Created_By') }}: {{ (version.insertedBy || {}).displayName }}</div>
                  <div>{{ $t('Created_On') }}: {{ $filters.DateTimeFilter(version.insertedByDate) }}</div>
                  <div>
                    <pre style="white-space: pre-wrap;">{{ version.comment || '' }}</pre>
                  </div>
                  <div>{{ $t('Update_Type') }}: {{ version.updateType || 'Document Updated.' }}</div>
                </v-col>
              </v-row>
            </template>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-btn color="info" text @click="showDocDetails = false">{{ $t('Close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <loading-dialog ref="loadingDialog"></loading-dialog>
  </div>
</template>

<script>
import ExternalService from '@/services/external.service'
import Toaster from '@/components/Toaster'
import LoadingDialog from '@/components/externalMocForm/LoadingDialog'

export default {
  name: 'SupportingDocuments',
  components: {
    Toaster,
    LoadingDialog
  },
  data() {
    return {
      show: false,
      documents: [],
      gettingDocuments: false,
      showDocDetails: false,
      detailsDoc: {},
      selectedFileNames: [],
      downloading: false,
      cancelDownloads: false,
      downloadCompleted: 0,
      downloadTotal: 0,
      downloadErrors: []
    }
  },
  props: {
    moc: {
      type: Object
    },
    showOpenText: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    dataTableHeaders: function() {
      return [
        { title: this.$t('Download'), key: 'download', sortable: false },
        { title: this.$t('File_Name'), key: 'fileName' },
        { title: this.$t('Modified_By'), key: 'modifiedBy' },
        { title: this.$t('Modified_On'), key: 'modifiedByDate' },
        ...(this.showOpenText ? [{ title: this.$t('Upload_To_OpenText'), key: 'uploadToOpenText', sortable: false }] : []),
        { title: '', sortable: false }
      ]
    },
    reversedDocVersions() {
      if (!this.detailsDoc.versions) return []
      return this.detailsDoc.versions.slice().reverse()
    },
    canShowShowButton: function() {
      return this.documents.length > 0
    },
    allSelected: function() {
      return this.documents.length > 0 && this.selectedFileNames.length === this.documents.length
    },
    someSelected: function() {
      return this.selectedFileNames.length > 0
    }
  },
  methods: {
    openTextValue: function(doc) {
      // null leaves both radios unselected so an unanswered document stays visibly unanswered
      if (doc.uploadToOpenText === true) return 'yes'
      if (doc.uploadToOpenText === false) return 'no'
      return null
    },
    getFiles: function() {
      if (!this.moc.mocId) return
      if (!this.gettingDocuments) {
        // get list of docs in S3 for this moc
        this.gettingDocuments = true
        return ExternalService.getMocSupportingDocs(this.moc.mocId)
          .then(data => {
            this.documents = data
          })
          .catch(error => {
            console.error(error)
            this.$refs.loadingDialog.error({ message: this.$t('Error_GetDocuments') })
          })
          .finally(() => {
            this.gettingDocuments = false
          })
      }
    },

    downloadDoc: function(doc, versionId) {
      this.$refs.loadingDialog.show()
      return new Promise((resolve, reject) => {
        ExternalService.downloadSupportingDoc(this.moc.mocId, doc.fileName, versionId)
          .then(url => {
            this.$refs.loadingDialog.hide()
            this.saveFromUrl(url, doc.fileName)
            resolve()
          })
          .catch(error => {
            console.error(error)
            this.$refs.loadingDialog.error({ message: this.$t('Error_DownloadDocument') })
            reject(error)
          })
          .finally(() => {
            this.$refs.loadingDialog.hide()
          })
      })
    },
    saveFromUrl: function(url, fileName) {
      const a = document.createElement('a')
      a.download = fileName
      a.href = url
      a.style.display = 'none'
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    toggleSelectAll: function(value) {
      this.selectedFileNames = value ? this.documents.map(doc => doc.fileName) : []
    },
    pause: function(milliseconds) {
      return new Promise(resolve => setTimeout(resolve, milliseconds))
    },
    cancelDownloadSelected: function() {
      this.cancelDownloads = true
    },
    // pre-signed urls let the browser stream each file straight from S3, so nothing is buffered here or in the services
    downloadSelected: async function() {
      const docs = this.documents.filter(doc => this.selectedFileNames.includes(doc.fileName))
      if (!docs.length || this.downloading) return

      this.cancelDownloads = false
      this.downloadErrors = []
      this.downloadCompleted = 0
      this.downloadTotal = docs.length
      this.downloading = true

      const batchSize = 4
      for (let i = 0; i < docs.length && !this.cancelDownloads; i += batchSize) {
        // sign a small batch just before use so links can't expire while a long list is queued
        const batch = await Promise.all(
          docs.slice(i, i + batchSize).map(doc =>
            ExternalService.downloadSupportingDoc(this.moc.mocId, doc.fileName)
              .then(url => ({ doc, url }))
              .catch(() => ({ doc, url: null }))
          )
        )

        for (const { doc, url } of batch) {
          if (this.cancelDownloads) break
          if (url) {
            this.saveFromUrl(url, doc.fileName)
          } else {
            this.downloadErrors.push(doc.fileName)
          }
          this.downloadCompleted++
          // staggered so each transfer has time to commit before the next one starts
          await this.pause(700)
        }
      }

      this.downloading = false
      if (this.downloadErrors.length) {
        this.$refs.toaster.pop('error', `${this.$t('Error_DownloadDocument')}: ${this.downloadErrors.join(', ')}`)
      }
    },
    setupDocDetails: function(doc) {
      this.detailsDoc = doc
      this.showDocDetails = true
    }
  },
  created() {
    this.getFiles()
  },
  watch: {
    documents(newValue) {
      const fileNames = (newValue || []).map(doc => doc.fileName)
      this.selectedFileNames = this.selectedFileNames.filter(name => fileNames.includes(name))
    }
  }
}
</script>

<style scoped>
.noUnder {
  text-decoration: none;
}
.title {
  font-size: 0.95rem !important;
  font-weight: 400;
}
.dropBox {
  border: 1px solid gray;
}
.openTextLabel {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
}
</style>
